import { useGetCustomerState } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { Constants } from '@constants';
import { ErrorIcon } from '@icons';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    Button,
    Input,
    currencyToNumber,
    Text,
    Spinner,
    TextArea
} from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { ErrorText, Label, Select } from '@ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { mapDurationAmountDE } from './CreateNewApplicationFormHelper';
import {
    TApplicationWithTurnover,
    useCreateNewApplication
} from './useCreateNewApplication';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { loanDurationOptionsDE } from '../../../applyFlexiLoanView/applyFlexiLoanFormView/loanDurationOptionsDE';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';

export type TApplicationStatus =
    | 'auto_approved'
    | 'auto_declined'
    | 'requires_documents'
    | 'docs_complete'
    | 'unknown'
    | null;

type TFormValues = {
    turnover: string;
    credit: number;
    purpose?: TApplicationWithTurnover['purpose'];
    detailedPurpose?: TApplicationWithTurnover['detailed_purpose'];
    duration?:
        | 'few_months'
        | 'ongoing'
        | 'one_to_two_years'
        | 'three_plus_years';
};

export const CreateNewApplicationForm = ({
    accountId,
    setApplicationStatus,
    amountFieldTitle,
    turnoverFieldTitle,
    onSuccess,
    showLoadingWhileSubmitting = true
}: {
    accountId: string;
    setApplicationStatus: React.Dispatch<
        React.SetStateAction<TApplicationStatus>
    >;
    amountFieldTitle?: React.ReactElement;
    turnoverFieldTitle?: React.ReactElement;
    onSuccess: () => void | Promise<void>;
    showLoadingWhileSubmitting?: boolean;
}) => {
    const intl = useIntl();
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));

    const {
        data: customerStateResponse,
        isLoading: customerStateLoading,
        isError: customerStateError
    } = useGetCustomerState(stateKey);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid, isSubmitting }
    } = useForm<TFormValues>({
        mode: 'onChange'
    });
    const { createNewApplication } = useCreateNewApplication(stateKey);

    if (accountsLoading || customerStateLoading)
        return (
            <div className="flex justify-center">
                <Spinner dataTestId="topup-form-loading" />
            </div>
        );
    if (accountsError || customerStateError) return <FallbackErrorModal />;

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const currentPrincipal = account.pending_balance!.principal!;

    const turnover =
        customerStateResponse?.application?.company?.last_12_months_turnover
            ?.amount;

    const currency = {
        locale: intl.locale,
        currencySymbol: true,
        minorUnits: false
    };

    const maxRequestAmountMinusCurrentPrincipal =
        Constants.MAX_LOAN_AMOUNT - currentPrincipal;

    const onSubmit: SubmitHandler<TFormValues> = async (formData) => {
        tracking.ampli.topUpFormSubmitButtonClicked({
            account_id: accountId
        });

        // Combining the outstanding balance and requested amount to better reflect what
        // we think customers think they ask for
        const combinedPrincipalAndRequestedAmount =
            formData.credit + currentPrincipal;

        const application: TApplicationWithTurnover = {
            amount: combinedPrincipalAndRequestedAmount,
            product_type: 'credit_facility',
            requested_cash_amount: formData.credit,
            company_turnover: Number(formData.turnover)
        };

        // Add DE questions to payload
        if (formData.purpose) {
            application.purpose = formData.purpose;

            if (formData.purpose === 'other') {
                application.detailed_purpose = formData.detailedPurpose;
            }
        }

        if (formData.duration) {
            application.duration = {
                amount: mapDurationAmountDE(formData.duration),
                unit: 'months'
            };
            application.qualitative_duration = formData.duration;
        }

        try {
            await createNewApplication(application);

            await onSuccess();
        } catch (err) {
            console.log(err);
        }
    };

    if (showLoadingWhileSubmitting && isSubmitting)
        return (
            <div className="flex flex-col items-center justify-center">
                <LoadingAnimation data-testid="loading-spinner" />
                <Text>
                    <FormattedMessage
                        id="_.create_new_application_form.submitting_message"
                        defaultMessage=" Please wait, it can take up to 30 seconds to submit your application"
                    />
                </Text>
            </div>
        );

    const watchCredit = watch('credit');
    const availableCash = account.latest_formal_offer?.available_cash!;
    const isPreApprovedOffer = account.latest_formal_offer?.is_pre_approval;

    const isPreApprovedWithAvailableCash =
        isPreApprovedOffer && availableCash > 0;
    const requestedAmountLessThanAvailableCash =
        isPreApprovedWithAvailableCash &&
        watchCredit >= 1000 &&
        watchCredit <= availableCash;

    const showDetailedPurpose = watch('purpose') === 'other';

    return (
        <form
            data-testid="create-new-application-form"
            className="flex w-full flex-col gap-2xl"
            onSubmit={handleSubmit(onSubmit)}
        >
            <Input
                {...register('credit', {
                    setValueAs: (val) => currencyToNumber(val, currency),
                    required: intl.formatMessage({
                        id: '_.createNewApplicationForm.credit_Error',
                        defaultMessage: 'Please enter how much is required'
                    }),
                    min: {
                        value: 1000,
                        message: intl.formatMessage(
                            {
                                id: '_.createNewApplicationForm.min_credit_Error',
                                defaultMessage:
                                    'Please enter an amount greater than {minAmount}'
                            },
                            {
                                minAmount: intlFormatCurrency({
                                    intl: intl,
                                    value: 1000
                                })
                            }
                        )
                    },
                    max: {
                        value: maxRequestAmountMinusCurrentPrincipal,
                        message: intl.formatMessage(
                            {
                                id: '_.createNewApplicationForm.max_credit_Error',
                                defaultMessage:
                                    'The maximum you can request is {maxAmount}'
                            },
                            {
                                maxAmount: intlFormatCurrency({
                                    intl: intl,
                                    value: maxRequestAmountMinusCurrentPrincipal,
                                    decimalPlaces: 2
                                })
                            }
                        )
                    }
                })}
                id="credit"
                label={
                    amountFieldTitle ||
                    intl.formatMessage({
                        id: '_.createNewApplicationForm.credit_question',
                        defaultMessage:
                            'How much more would you like to borrow?'
                    })
                }
                type="currency"
                placeholder={`e.g. ${intlFormatCurrency({
                    intl: intl,
                    value: 10000
                })}`}
                currency={currency}
                error={errors.credit?.message}
                // @ts-expect-error
                success={
                    (requestedAmountLessThanAvailableCash &&
                        intl.formatMessage(
                            {
                                id: '_.createNewApplicationForm.alreadyApprovedMessage',
                                defaultMessage:
                                    "You're already approved for {approvalAmount} - you can take your funds {hereCta}"
                            },
                            {
                                approvalAmount: intlFormatCurrency({
                                    intl,
                                    value: availableCash,
                                    decimalPlaces: 2
                                }),
                                hereCta: (
                                    <span
                                        onClick={() =>
                                            setApplicationStatus(
                                                'auto_approved'
                                            )
                                        }
                                        className="ml-xs text-primary-40 underline hover:cursor-pointer"
                                    >
                                        here
                                    </span>
                                )
                            }
                        )) ||
                    ''
                }
            />
            <Input
                {...register('turnover', {
                    setValueAs: (val) => currencyToNumber(val, currency),
                    required: intl.formatMessage({
                        id: '_.createNewApplicationForm.turnover_Error',
                        defaultMessage: 'Turnover is Required'
                    }),
                    min: {
                        value: 1,
                        message: intl.formatMessage(
                            {
                                id: '_.createNewApplicationForm.minTurnoverError',
                                defaultMessage:
                                    'Please enter an amount greater than {minAmount}'
                            },
                            {
                                minAmount: intlFormatCurrency({
                                    intl: intl,
                                    value: 0
                                })
                            }
                        )
                    }
                })}
                id="turnover"
                label={
                    turnoverFieldTitle ||
                    intl.formatMessage({
                        id: '_.createNewApplicationForm.turnover_question',
                        defaultMessage:
                            'How much did your business turnover in the last 12 months?'
                    })
                }
                type="currency"
                currency={currency}
                placeholder={`e.g. ${intlFormatCurrency({
                    intl: intl,
                    value: 100000
                })}`}
                defaultValue={turnover}
                error={errors.turnover?.message}
            />

            {getCountry() === 'DE' && (
                <>
                    <div>
                        <Label className="mb-m block" htmlFor="purpose">
                            <FormattedMessage
                                id="_.createNewApplicationForm.purpose_question"
                                defaultMessage="What do you want to use the loan for?"
                            />
                        </Label>

                        <Select
                            className="h-3xl w-full"
                            id="purpose"
                            {...register('purpose', {
                                required: intl.formatMessage({
                                    id: '_.createNewApplicationForm.purpose_error',
                                    defaultMessage: 'Please select a purpose'
                                })
                            })}
                        >
                            <option value="" hidden>
                                {intl.formatMessage({
                                    id: '_.createNewApplicationForm.default_option',
                                    defaultMessage: 'Please select an option'
                                })}
                            </option>

                            {loanPurposeOptionsDE.map(({ label, value }) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </Select>

                        {errors.purpose && (
                            <div className="mt-xs flex items-center">
                                <ErrorIcon className="mr-xs" />
                                <ErrorText>{errors.purpose.message}</ErrorText>
                            </div>
                        )}
                    </div>

                    {showDetailedPurpose ? (
                        <div className="space-y-s">
                            <Label htmlFor="detailed-purpose">
                                <FormattedMessage
                                    id="_.create_new_application_form.additional_information"
                                    defaultMessage="Additional information"
                                />
                            </Label>
                            <TextArea
                                id="detailed-purpose"
                                {...register('detailedPurpose', {
                                    required: showDetailedPurpose
                                })}
                            />

                            {errors.detailedPurpose && (
                                <div className="mt-xs flex items-center">
                                    <ErrorIcon className="mr-xs" />
                                    <ErrorText>
                                        <FormattedMessage
                                            id="_.create_new_application_form.additional_information_validation"
                                            defaultMessage="Please enter a purpose"
                                        />
                                    </ErrorText>
                                </div>
                            )}
                        </div>
                    ) : null}

                    <div>
                        <Label className="mb-m block" htmlFor="duration">
                            <FormattedMessage
                                id="_.createNewApplicationForm.duration_question"
                                defaultMessage="How long do you need the loan for?"
                            />
                        </Label>

                        <Select
                            className="h-3xl w-full"
                            id="duration"
                            {...register('duration', {
                                required: intl.formatMessage({
                                    id: '_.createNewApplicationForm.duration_error',
                                    defaultMessage: 'Please select a duration'
                                })
                            })}
                        >
                            <option value="" hidden>
                                {intl.formatMessage({
                                    id: '_.createNewApplicationForm.default_option',
                                    defaultMessage: 'Please select an option'
                                })}
                            </option>

                            {loanDurationOptionsDE.map(({ label, value }) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </Select>

                        {errors.purpose && (
                            <div className="mt-xs flex items-center">
                                <ErrorIcon className="mr-xs" />
                                <ErrorText>{errors.purpose.message}</ErrorText>
                            </div>
                        )}
                    </div>
                </>
            )}

            <Button
                type="submit"
                variant="primary"
                disabled={!isValid || requestedAmountLessThanAvailableCash}
                loading={isSubmitting}
                className="mx-auto my-0 w-max"
            >
                <FormattedMessage
                    id="_.createNewApplicationForm.next_button"
                    defaultMessage="Continue"
                />
            </Button>
        </form>
    );
};

const loanPurposeOptionsDE = [
    { label: 'Warenbestand erhöhen', value: 'stock_purchase' },
    { label: 'Zwischenfinanzierung', value: 'bridging_loan' },
    { label: 'Marketing', value: 'marketing' },
    {
        label: 'Maschinenkauf',
        value: 'equipment_purchase'
    },
    { label: 'Verbindlichkeiten begleichen', value: 'pay_bill' },
    { label: 'Kredite umschulden', value: 'refinancing_debt' },
    { label: 'Sonstige Verwendung', value: 'other' }
];
